import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Bookmark, DollarSign, Share2 } from 'lucide-react';
import clsx from 'clsx';
import { useAppStore } from '../../store/useAppStore';

export function MobileActionSheet() {
  const {
    actionSheetOpen,
    actionSheetRec,
    closeActionSheet,
    isWatched,
    addToWatchlist,
    removeFromWatchlist,
    openWorkspaceDrawer,
  } = useAppStore();

  useEffect(() => {
    if (actionSheetOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [actionSheetOpen]);

  if (!actionSheetOpen || !actionSheetRec) return null;

  const rec = actionSheetRec;
  const watched = isWatched(rec.eventId);

  async function handleWatch() {
    if (watched) {
      await removeFromWatchlist(rec.eventId);
    } else {
      await addToWatchlist(rec.eventId, rec.id);
    }
    closeActionSheet();
  }

  function handleBet() {
    closeActionSheet();
    openWorkspaceDrawer(rec);
  }

  function handleShare() {
    const text = `${rec.awayTeamName} @ ${rec.homeTeamName} — ${rec.selection} ${rec.odds > 0 ? '+' : ''}${rec.odds} (${Math.round((rec.confidence ?? 0) * 100)}% confidence)`;
    if (navigator.share) {
      void navigator.share({ title: 'NavyrxSports Intelligence', text });
    } else {
      void navigator.clipboard.writeText(text);
    }
    closeActionSheet();
  }

  const ACTIONS = [
    {
      label: watched ? 'Remove from Watchlist' : 'Add to Watchlist',
      icon: Bookmark,
      cls: watched ? 'text-warn' : 'text-gray-200',
      action: handleWatch,
    },
    {
      label: 'Place Bet',
      icon: DollarSign,
      cls: 'text-win',
      action: handleBet,
    },
    {
      label: 'Share',
      icon: Share2,
      cls: 'text-brand',
      action: handleShare,
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 z-[70] bg-black/60 md:hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={closeActionSheet}
      />

      {/* Sheet */}
      <motion.div
        className="fixed inset-x-0 bottom-0 z-[70] md:hidden bg-surface-1 rounded-t-2xl border-t border-surface-4"
        initial={{ y: 300 }}
        animate={{ y: 0 }}
        exit={{ y: 300 }}
        transition={{ type: 'spring', stiffness: 400, damping: 40 }}
      >
        {/* Handle */}
        <div className="flex items-center justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-surface-4" />
        </div>

        {/* Title */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-100 truncate">
              {rec.awayTeamName} @ {rec.homeTeamName}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {rec.market} · {rec.selection}
            </p>
          </div>
          <button
            onClick={closeActionSheet}
            className="p-1.5 ml-2 flex-shrink-0 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-surface-3 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Actions */}
        <div className="px-2 pb-6 space-y-1">
          {ACTIONS.map(({ label, icon: Icon, cls, action }) => (
            <button
              key={label}
              onClick={() => void action()}
              className={clsx(
                'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-colors hover:bg-surface-3 active:scale-[0.98]',
                cls,
              )}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {label}
            </button>
          ))}

          <button
            onClick={closeActionSheet}
            className="w-full mt-3 py-3 rounded-xl text-sm font-medium text-gray-500 bg-surface-2 hover:bg-surface-3 transition-colors active:scale-[0.98]"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </>
  );
}
